import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './LaunchPage.module.css';
import { buildStorageUrl } from '../config/firebaseStorage';
import { Helmet } from '../utils/helmet';
import { useTranslation } from 'react-i18next';


// Helper component for tracking visibility
const FeatureRow = ({ children, className }) => {
  const [isVisible, setIsVisible] = useState(false);
  const rowRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // "Light up" when it crosses a threshold (e.g. 30% visible)
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
            // Optional: fade out when leaving view
            setIsVisible(false);
        }
      },
      { threshold: 0.3 } 
    );

    if (rowRef.current) observer.observe(rowRef.current);
    return () => {
      if (rowRef.current) observer.unobserve(rowRef.current);
    };
  }, []);

  return (
    <div 
      ref={rowRef} 
      className={`${className} ${isVisible ? styles.featureRowVisible : ''}`}
    >
      {children}
    </div>
  );
};

function LaunchPage() {
  const { t } = useTranslation();
  const desktopBg = buildStorageUrl('other/badass.jpg');
  const mobileBg = buildStorageUrl('other/portrait.jpg');
  const heroRef = useRef(null);
  const [heroOpacity, setHeroOpacity] = useState(1);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detect dark mode preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);

    const handleChange = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Scroll-based fade effect for hero background
  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;
      const heroHeight = heroRef.current.offsetHeight;
      const scrollY = window.scrollY;
      // Fade from 1 to 0.6 (never fully fade out)
      const opacity = Math.max(0.6, 1 - (scrollY / heroHeight) * 0.4);
      setHeroOpacity(opacity);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Feature images - light/dark mode variants
  const feature1Image = buildStorageUrl(isDarkMode ? 'other/mountainGridDark.png' : 'other/mountainGridLight.png');
  const feature2Image = buildStorageUrl(isDarkMode ? 'other/newGearDark.png' : 'other/newGearLight.png');
  const feature3Image = buildStorageUrl(isDarkMode ? 'other/chatDark.png' : 'other/chatLite.png');
  const feature4Image = buildStorageUrl(isDarkMode ? 'other/postDark.png' : 'other/postLight.png');
  const partnerLogos = [
    { name: 'Mammut', src: buildStorageUrl('logos/mammut.png') },
    { name: 'Salomon', src: buildStorageUrl('logos/salomon.jpg') },
    { name: 'Gigasport', src: buildStorageUrl('logos/gigasport.jpg') },
    { name: 'Bergzeit', src: buildStorageUrl('logos/bergzeit.png') },
    { name: 'Vertical Extreme', src: buildStorageUrl('logos/vertical.jpg') },
    { name: 'Bever.nl', src: buildStorageUrl('logos/bever.jpg') }
  ];

  return (
    <>
      <Helmet>
        <title>{t('common.siteName')} | {t('launch.tagline')}</title>
        <meta name="description" content={t('launch.helmetDescription')} />
      </Helmet>
      
      <div className={styles.launchPage}>
        {/* Fixed Background */}
        <div 
          className={styles.fixedBackground}
          style={{
            '--launch-bg-image': `url("${desktopBg}")`,
            '--launch-mobile-image': `url("${mobileBg}")`,
            '--hero-opacity': heroOpacity,
          }}
        />
        <div className={styles.fixedOverlay} />

        {/* Hero Section */}
        <section
          ref={heroRef}
          className={styles.heroSection}
        >
          <div className={styles.heroContent}>
            <h1 className={styles.title}>{t('common.siteName')}</h1>
            <p className={styles.tagline}>Your mountain toolkit</p>
            <Link to="/browse" className={styles.ctaButton}>
              {t('launch.getStarted')}
            </Link>
          </div>
          <div className={styles.scrollIndicator}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          </div>
        </section>

        {/* Features Section */}
        <section className={styles.featuresSection}>
          
          {/* Feature 1 - Text Left, Image Right */}
          <FeatureRow className={`${styles.featureRow} ${styles.featureRowNormal}`}>
            <div className={styles.featureText}>
              <h2 className={styles.featureTitle}>{t('launch.feature1Title')}</h2>
              <p className={styles.featureDescription}>{t('launch.feature1Description')}</p>
            </div>
            <div className={styles.featureImageWrapper}>
              <img src={feature1Image} alt="" className={styles.featureImage} loading="lazy" />
            </div>
          </FeatureRow>

          {/* Feature 2 - Image Left, Text Right */}
          <FeatureRow className={`${styles.featureRow} ${styles.featureRowReverse}`}>
             <div className={styles.featureText}>
              <h2 className={styles.featureTitle}>{t('launch.feature2Title')}</h2>
              <p className={styles.featureDescription}>{t('launch.feature2Description')}</p>
              <h2 className={`${styles.featureTitle} ${styles.partnerTitle}`}>In partnership with</h2>
              <div className={styles.partnerLogos}>
                {partnerLogos.map(logo => (
                  <img
                    key={logo.name}
                    src={logo.src}
                    alt={logo.name}
                    className={`${styles.partnerLogo} ${logo.name === 'Mammut' ? styles.partnerLogoLarge : ''}`}
                    loading="lazy"
                  />
                ))}
              </div>
            </div>
            <div className={styles.featureImageWrapper}>
              <img src={feature2Image} alt="" className={styles.featureImage} loading="lazy" />
            </div>
          </FeatureRow>

          {/* Feature 3 - Text Left, Image Right */}
          <FeatureRow className={`${styles.featureRow} ${styles.featureRowNormal}`}>
            <div className={styles.featureText}>
              <h2 className={styles.featureTitle}>{t('launch.feature3Title')}</h2>
              <p className={styles.featureDescription}>{t('launch.feature3Description')}</p>
            </div>
            <div className={styles.featureImageWrapper}>
              <img src={feature3Image} alt="" className={styles.featureImage} loading="lazy" />
            </div>
          </FeatureRow>

          {/* Feature 4 - Image Left, Text Right */}
          <FeatureRow className={`${styles.featureRow} ${styles.featureRowReverse}`}>
            <div className={styles.featureText}>
              <h2 className={styles.featureTitle}>{t('launch.feature4Title')}</h2>
              <p className={styles.featureDescription}>{t('launch.feature4Description')}</p>
            </div>
            <div className={styles.featureImageWrapper}>
              <img src={feature4Image} alt="" className={styles.featureImage} loading="lazy" />
            </div>
          </FeatureRow>

        </section>

        {/* Final CTA Section */}
        <section className={styles.ctaSection}>
          <h2 className={styles.ctaSectionTitle}>{t('launch.ctaTitle')}</h2>
          <Link to="/browse" className={styles.ctaButton}>
            {t('launch.getStarted')}
          </Link>
        </section>

        {/* Footer */}
        <footer className={styles.footer}>
          <Link to="/privacy" className={styles.footerLink}>{t('common.privacyPolicy')}</Link>
          <Link to="/terms" className={styles.footerLink}>{t('common.termsOfService')}</Link>
        </footer>
      </div>
    </>
  );
}

export default LaunchPage;
